/**
 * Helpers for the grouped curriculum progress view (業種/コース → WEEK list).
 * Admin-side port of the learner app's dashboard helpers.
 */

import {
  TCurriculumGroupDto,
  TCurriculumProgressData,
  TSubjectCurriculumDto,
  TSubjectStatusDto,
} from "@/api/services/main/learning-progress/dtos/get-learner-curriculum-progress.dto";

/** Week number inside a subject name, e.g. "Week3_建設基礎知識" → 3 */
const WEEK_NUMBER_REGEX = /week\s*(\d+)/i;

/** Per-status WEEK counts of one group, plus the total the x/y counter divides by */
export type TCurriculumGroupCounts = {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
};

/**
 * Normalize the response into groups.
 * BE grouping wins; otherwise the flat subject list becomes a single group so the
 * same UI renders on both contracts.
 */
export const toCurriculumGroups = (progress: TCurriculumProgressData): TCurriculumGroupDto[] => {
  if (progress.groups?.length) return progress.groups;
  if (!progress.subjects.length) return [];

  // Flat contract — 業種 name titles the single group, curriculum name as fallback
  return [{ id: 0, name: progress.industryName ?? progress.curriculumName ?? "", subjects: progress.subjects }];
};

/** Count WEEKs per status — derived from the subjects themselves so bar, chips and legend always agree */
export const countGroupStatuses = (subjects: Pick<TSubjectCurriculumDto, "status">[]): TCurriculumGroupCounts => {
  const tally = (status: TSubjectStatusDto) => subjects.filter((subject) => subject.status === status).length;

  return {
    total: subjects.length,
    completed: tally("completed"),
    inProgress: tally("in_progress"),
    notStarted: tally("not_started"),
  };
};

/** WEEK number of a subject — parsed from its name, falling back to its position */
const getWeekNumber = (subject: Pick<TSubjectCurriculumDto, "name">, index: number): number => {
  const match = subject.name.match(WEEK_NUMBER_REGEX);
  return match ? Number(match[1]) : index + 1;
};

/** Chip label for a WEEK — e.g. "W3" */
export const getWeekLabel = (subject: Pick<TSubjectCurriculumDto, "name">, index: number): string => `W${getWeekNumber(subject, index)}`;
